import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import SettingSection from "@/modules/shared/SettingSection"
import SettingContentWrapper from "@/modules/shared/SettingContentWrapper"
import { Checkbox, Form, Switch, message } from "antd"
import { Loader } from "@/UI"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import FormItem from "antd/lib/form/FormItem"
import { useRecoilValue } from "recoil"
import { isPayingUserSelector, userAtom } from "@/atoms/userAtom"
import { AppConfiguration } from "@/types/drivesAppSettings"
import { useDriverSettings } from "@/hooks/useDriverSettings"
import { useFlags } from "flagsmith/react"
import { LabelWithTooltip } from "@/UI/Labels/LabelWithTooltip"

interface DriverAppSettingsProps {}

const DriverAppSettings: React.FC<DriverAppSettingsProps> = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const flags = useFlags(["show_print_labels", "nash_edg_demo"])

  const { org } = useRecoilValue(userAtom)
  const isPayingUser = useRecoilValue(isPayingUserSelector)
  const { driverSettings, isLoading, fetchDriverSettings, updateDriverAppSettings } =
    useDriverSettings()

  // ENG Demo
  const [driverRequirementsEnabled, setDriverRequirementsEnabled] = useState<boolean>(false)
  const [requireRecipientId, setRequireRecipientId] = useState<boolean>(false)
  const [nominatedItems, setNominatedItems] = useState<boolean>(false)

  const emptyAppConfig: AppConfiguration = {
    scanBarcodeForPickupRequired: false,
    markParcelsForPickupRequired: false,
    proofOfPickupRequired: false,
    proofOfPickupSignatureRequired: false,
    proofOfPickupPhotoRequired: false,
    proofOfDeliveryRequired: false,
    driverCanOptimizeRoutes: false,
    proofOfDeliverySignatureRequired: false,
    proofOfDeliveryPhotoRequired: false,
    proofOfFailureRequired: false,
    canAcceptRejectOrders: false,
    triggerReturnAfterFailure: false,
    automaticallyStartOrderWhenAccepted: false,
    hideDriverPayInTheApp: false,
    allowDriversToEditAddress: false,
    canChangeAddressForOngoingOrders: false,
  }

  const scanBarcodeForPickupRequired = Form.useWatch("scanBarcodeForPickupRequired", form)
  const markParcelsForPickupRequired = Form.useWatch("markParcelsForPickupRequired", form)
  const driverCanOptimizeRoutes = Form.useWatch("driverCanOptimizeRoutes", form)
  const proofOfPickupRequired = Form.useWatch("proofOfPickupRequired", form)
  const proofOfPickupSignatureRequired = Form.useWatch("proofOfPickupSignatureRequired", form)
  const proofOfPickupPhotoRequired = Form.useWatch("proofOfPickupPhotoRequired", form)
  const proofOfPickupSwitchEnabled =
    proofOfPickupRequired || proofOfPickupSignatureRequired || proofOfPickupPhotoRequired

  const proofOfDeliveryRequired = Form.useWatch("proofOfDeliveryRequired", form)
  const proofOfDeliverySignatureRequired = Form.useWatch("proofOfDeliverySignatureRequired", form)
  const proofOfDeliveryPhotoRequired = Form.useWatch("proofOfDeliveryPhotoRequired", form)
  const proofOfDeliverySwitchEnabled =
    proofOfDeliveryRequired || proofOfDeliverySignatureRequired || proofOfDeliveryPhotoRequired

  const proofOfFailureRequired = Form.useWatch("proofOfFailureRequired", form)

  const canAcceptRejectOrders = Form.useWatch("canAcceptRejectOrders", form)
  const allowDriversToEditAddress = Form.useWatch("allowDriversToEditAddress", form)
  const canChangeAddressForOngoingOrders = Form.useWatch("canChangeAddressForOngoingOrders", form)
  const triggerReturnAfterFailure = Form.useWatch("triggerReturnAfterFailure", form)
  const automaticallyStartOrderWhenAccepted = Form.useWatch(
    "automaticallyStartOrderWhenAccepted",
    form
  )
  const hideDriverPayInTheApp = Form.useWatch("hideDriverPayInTheApp", form)
  const isModifying =
    scanBarcodeForPickupRequired !== driverSettings.driverApp?.scanBarcodeForPickupRequired ||
    markParcelsForPickupRequired !== driverSettings.driverApp?.markParcelsForPickupRequired ||
    driverCanOptimizeRoutes !== driverSettings.driverApp?.driverCanOptimizeRoutes ||
    proofOfPickupRequired !== driverSettings.driverApp?.proofOfPickupRequired ||
    proofOfPickupSignatureRequired !== driverSettings.driverApp?.proofOfPickupSignatureRequired ||
    proofOfPickupPhotoRequired !== driverSettings.driverApp?.proofOfPickupPhotoRequired ||
    proofOfDeliveryRequired !== driverSettings.driverApp?.proofOfDeliveryRequired ||
    proofOfDeliverySignatureRequired !==
      driverSettings.driverApp?.proofOfDeliverySignatureRequired ||
    proofOfDeliveryPhotoRequired !== driverSettings.driverApp?.proofOfDeliveryPhotoRequired ||
    proofOfFailureRequired !== driverSettings.driverApp?.proofOfFailureRequired ||
    canAcceptRejectOrders !== driverSettings.driverApp?.canAcceptRejectOrders ||
    triggerReturnAfterFailure !== driverSettings.driverApp?.triggerReturnAfterFailure ||
    automaticallyStartOrderWhenAccepted !==
      driverSettings.driverApp?.automaticallyStartOrderWhenAccepted ||
    hideDriverPayInTheApp !== driverSettings.driverApp?.hideDriverPayInTheApp ||
    allowDriversToEditAddress !== driverSettings.driverApp?.allowDriversToEditAddress ||
    canChangeAddressForOngoingOrders !== driverSettings.driverApp?.canChangeAddressForOngoingOrders

  useEffect(() => {
    form.setFieldsValue(driverSettings.driverApp || emptyAppConfig)
  }, [driverSettings.driverApp])

  useEffect(() => {
    fetchDriverSettings()
  }, [org?.id])

  const onFinish = async (formValues: AppConfiguration) => {
    try {
      await updateDriverAppSettings({
        scanBarcodeForPickupRequired: formValues.scanBarcodeForPickupRequired,
        markParcelsForPickupRequired: formValues.markParcelsForPickupRequired,
        proofOfPickupRequired: formValues.proofOfPickupRequired,
        proofOfPickupSignatureRequired: formValues.proofOfPickupSignatureRequired,
        proofOfPickupPhotoRequired: formValues.proofOfPickupPhotoRequired,
        proofOfDeliveryRequired: formValues.proofOfDeliveryRequired,
        driverCanOptimizeRoutes: formValues.driverCanOptimizeRoutes,
        proofOfDeliverySignatureRequired: formValues.proofOfDeliverySignatureRequired,
        proofOfDeliveryPhotoRequired: formValues.proofOfDeliveryPhotoRequired,
        proofOfFailureRequired: formValues.proofOfFailureRequired,
        canAcceptRejectOrders: formValues.canAcceptRejectOrders,
        triggerReturnAfterFailure: formValues.triggerReturnAfterFailure,
        automaticallyStartOrderWhenAccepted: formValues.automaticallyStartOrderWhenAccepted,
        hideDriverPayInTheApp: formValues.hideDriverPayInTheApp,
        allowDriversToEditAddress: formValues.allowDriversToEditAddress,
        canChangeAddressForOngoingOrders: formValues.canChangeAddressForOngoingOrders,
      })
      message.success(t("common.form.updatedPreferencesSuccess"))
    } catch (err) {
      message.error(t("common.form.updatedPreferencesFailure"))
    }
  }

  return (
    <SettingSection title={t("profile.common.driverAppSettings")}>
      <SettingContentWrapper>
        {!driverSettings.driverApp ? (
          <Loader fontSize={40} color="#4100FF" size="small" />
        ) : (
          <Form
            id="driver-app-settings-form"
            className="flex flex-col w-full gap-y-4"
            form={form}
            onFinish={onFinish}
            layout="vertical"
            disabled={!isPayingUser}
          >
            {flags.show_print_labels.enabled && (
              <div className="flex gap-x-3">
                <FormItem
                  name="scanBarcodeForPickupRequired"
                  className="mb-0"
                  valuePropName="checked"
                >
                  <Switch />
                </FormItem>
                <LabelWithTooltip
                  label={t("profile.DriverAppSettings.scanBarcodeForPickupRequired")}
                  tooltipContent={t("profile.DriverAppSettings.barcodeForPickupRequiredTooltip")}
                />
              </div>
            )}
            <div className="flex gap-x-3">
              <FormItem
                name="markParcelsForPickupRequired"
                className="mb-0"
                valuePropName="checked"
              >
                <Switch />
              </FormItem>
              <LabelWithTooltip
                label={t("profile.DriverAppSettings.markParcelsForPickupRequired")}
                tooltipContent={t("profile.DriverAppSettings.markParcelsForPickupRequiredTooltip")}
              />
            </div>
            {/* ENG Demo */}
            {flags.nash_edg_demo.enabled && (
              <>
                <div className="flex gap-x-3 items-center">
                  <Form.Item name="requireRecipientId" className="mb-0">
                    <Switch
                      checked={requireRecipientId}
                      onChange={() => {
                        setRequireRecipientId(!requireRecipientId)
                      }}
                    />
                  </Form.Item>
                  <LabelWithTooltip
                    label={t("Require Recipient ID")}
                    tooltipContent={t(
                      "A Driver can only complete his stop after verifying a recipient ID"
                    )}
                  />
                </div>
                <div className="flex gap-x-3">
                  <Form.Item name="nominatedItems" className="mb-0">
                    <Switch
                      checked={nominatedItems}
                      onChange={() => {
                        setNominatedItems(!nominatedItems)
                      }}
                    />
                  </Form.Item>
                  <LabelWithTooltip
                    label={t("Require named items")}
                    tooltipContent={t("Items can only be picked-up by the mentioned recipient")}
                  />
                </div>
                <div className="flex gap-x-3">
                  <FormItem name="driverRequirements" className="mb-0">
                    <Switch
                      checked={driverRequirementsEnabled}
                      onChange={() => {
                        setDriverRequirementsEnabled(!driverRequirementsEnabled)
                      }}
                    />
                  </FormItem>
                  <LabelWithTooltip
                    label={t("Driver start checklist required")}
                    tooltipContent={t("profile.DriverAppSettings.proofOfPickupRequiredTooltip")}
                  />
                </div>
                <div className={`flex ${!driverRequirementsEnabled && "hidden"}`}>
                  <div className="w-[60px]" />
                  <div className="flex flex-col">
                    <FormItem className="mb-0">
                      <Checkbox className="!ml-0" defaultChecked={true}>
                        {t("common.identityVerification")}
                      </Checkbox>
                    </FormItem>
                    <FormItem className="mb-0">
                      <Checkbox className="!ml-0" defaultChecked={true}>
                        {t("common.vehiclePhysicalCondition")}
                      </Checkbox>
                    </FormItem>
                    <FormItem className="mb-0">
                      <Checkbox className="!ml-0" defaultChecked={true}>
                        {t("common.engine")}
                      </Checkbox>
                    </FormItem>
                    <FormItem className="mb-0">
                      <Checkbox className="!ml-0" defaultChecked={true}>
                        {t("common.mirrorsAndWindows")}
                      </Checkbox>
                    </FormItem>
                    <FormItem className="mb-0">
                      <Checkbox className="!ml-0" defaultChecked={true}>
                        {t("common.gasSystem")}
                      </Checkbox>
                    </FormItem>
                  </div>
                </div>
              </>
            )}
            {/* End of ENG Demo */}
            <div className="flex gap-x-3">
              <FormItem name="proofOfPickupRequired" className="mb-0">
                <Switch
                  checked={proofOfPickupSwitchEnabled}
                  onChange={(checked) => {
                    if (!checked) {
                      form.setFieldsValue({
                        proofOfPickupRequired: false,
                        proofOfPickupSignatureRequired: false,
                        proofOfPickupPhotoRequired: false,
                      })
                    }
                  }}
                />
              </FormItem>
              <LabelWithTooltip
                label={t("profile.DriverAppSettings.proofOfPickupRequired")}
                tooltipContent={t("profile.DriverAppSettings.proofOfPickupRequiredTooltip")}
              />
            </div>
            <div className={`flex ${!proofOfPickupSwitchEnabled && "hidden"}`}>
              <div className="w-[60px]" />
              <div className="flex flex-col">
                <FormItem name="proofOfPickupRequired" className="mb-0" valuePropName="checked">
                  <Checkbox
                    className="!ml-0"
                    onChange={(e) => {
                      if (e.target.checked) {
                        form.setFieldsValue({
                          proofOfPickupSignatureRequired: false,
                          proofOfPickupPhotoRequired: false,
                        })
                      }
                    }}
                  >
                    {t("profile.DriverAppSettings.requireSignatureOrPhoto")}
                  </Checkbox>
                </FormItem>
                <FormItem
                  name="proofOfPickupSignatureRequired"
                  className="mb-0"
                  valuePropName="checked"
                >
                  <Checkbox
                    className="!ml-0"
                    onChange={(e) => {
                      if (e.target.checked) {
                        form.setFieldsValue({
                          proofOfPickupRequired: false,
                          proofOfPickupPhotoRequired: false,
                        })
                      }
                    }}
                  >
                    {t("profile.DriverAppSettings.requireSignatureOnly")}
                  </Checkbox>
                </FormItem>
                <FormItem
                  name="proofOfPickupPhotoRequired"
                  className="mb-0"
                  valuePropName="checked"
                >
                  <Checkbox
                    className="!ml-0"
                    onChange={(e) => {
                      if (e.target.checked) {
                        form.setFieldsValue({
                          proofOfPickupRequired: false,
                          proofOfPickupSignatureRequired: false,
                        })
                      }
                    }}
                  >
                    {t("profile.DriverAppSettings.requirePhotoOnly")}
                  </Checkbox>
                </FormItem>
              </div>
            </div>
            <div className="flex flex-col gap-y-1">
              <div className="flex gap-x-3">
                <FormItem name="proofOfDeliveryRequired" className="mb-0">
                  <Switch
                    checked={proofOfDeliverySwitchEnabled}
                    onChange={(checked) => {
                      if (!checked) {
                        form.setFieldsValue({
                          proofOfDeliveryRequired: false,
                          proofOfDeliverySignatureRequired: false,
                          proofOfDeliveryPhotoRequired: false,
                        })
                      }
                    }}
                  />
                </FormItem>
                <LabelWithTooltip
                  label={t("profile.DriverAppSettings.proofOfDeliveryRequired")}
                  tooltipContent={t("profile.DriverAppSettings.proofOfDeliveryRequiredTooltip")}
                />
              </div>
              <div className={`flex ${!proofOfDeliverySwitchEnabled && "hidden"}`}>
                <div className="w-[60px]" />
                <div className="flex flex-col">
                  <FormItem name="proofOfDeliveryRequired" className="mb-0" valuePropName="checked">
                    <Checkbox
                      className="!ml-0"
                      onChange={(e) => {
                        if (e.target.checked) {
                          form.setFieldsValue({
                            proofOfDeliverySignatureRequired: false,
                            proofOfDeliveryPhotoRequired: false,
                          })
                        }
                      }}
                    >
                      {t("profile.DriverAppSettings.requireSignatureOrPhoto")}
                    </Checkbox>
                  </FormItem>
                  <FormItem
                    name="proofOfDeliverySignatureRequired"
                    className="mb-0"
                    valuePropName="checked"
                  >
                    <Checkbox
                      className="!ml-0"
                      onChange={(e) => {
                        if (e.target.checked) {
                          form.setFieldsValue({
                            proofOfDeliveryRequired: false,
                            proofOfDeliveryPhotoRequired: false,
                          })
                        }
                      }}
                    >
                      {t("profile.DriverAppSettings.requireSignatureOnly")}
                    </Checkbox>
                  </FormItem>
                  <FormItem
                    name="proofOfDeliveryPhotoRequired"
                    className="mb-0"
                    valuePropName="checked"
                  >
                    <Checkbox
                      className="!ml-0"
                      onChange={(e) => {
                        if (e.target.checked) {
                          form.setFieldsValue({
                            proofOfDeliveryRequired: false,
                            proofOfDeliverySignatureRequired: false,
                          })
                        }
                      }}
                    >
                      {t("profile.DriverAppSettings.requirePhotoOnly")}
                    </Checkbox>
                  </FormItem>
                </div>
              </div>
            </div>
            <div className="flex gap-x-3">
              <FormItem name="proofOfFailureRequired" className="mb-0" valuePropName="checked">
                <Switch />
              </FormItem>
              <LabelWithTooltip
                label={t("profile.DriverAppSettings.proofOfFailureRequired")}
                tooltipContent={t("profile.DriverAppSettings.proofOfFailureRequiredTooltip")}
              />
            </div>
            <div className="flex gap-x-3">
              <FormItem name="canAcceptRejectOrders" className="mb-0" valuePropName="checked">
                <Switch
                  checked={canAcceptRejectOrders}
                  onChange={(checked) => {
                    if (!checked) {
                      form.setFieldsValue({
                        automaticallyStartOrderWhenAccepted: false,
                      })
                    }
                  }}
                />
              </FormItem>
              <LabelWithTooltip
                label={t("profile.DriverAppSettings.canAcceptRejectOrders")}
                tooltipContent={t("profile.DriverAppSettings.canAcceptRejectOrdersTooltip")}
              />
            </div>
            <div className={`flex ${!canAcceptRejectOrders && "hidden"}`}>
              <div className="w-[60px]" />
              <div className="flex gap-x-3">
                <FormItem
                  name="automaticallyStartOrderWhenAccepted"
                  className="mb-0"
                  valuePropName="checked"
                >
                  <Checkbox className="!ml-0">
                    {t("profile.DriverAppSettings.automaticallyStartOrderWhenAccepted")}
                  </Checkbox>
                </FormItem>
              </div>
            </div>
            <div className="flex gap-x-3">
              <FormItem name="allowDriversToEditAddress" className="mb-0" valuePropName="checked">
                <Switch
                  checked={allowDriversToEditAddress}
                  onChange={(checked) => {
                    if (!checked) {
                      form.setFieldsValue({
                        allowDriversToEditAddress: false,
                      })
                    }
                  }}
                />
              </FormItem>
              <LabelWithTooltip
                label={t("profile.DriverAppSettings.allowDriversToEditAddress")}
                tooltipContent={t("profile.DriverAppSettings.allowDriversToEditAddressTooltip")}
              />
            </div>
            <div className={`flex ${!allowDriversToEditAddress && "hidden"}`}>
              <div className="w-[60px]" />
              <div className="flex gap-x-3">
                <FormItem
                  name="canChangeAddressForOngoingOrders"
                  className="mb-0"
                  valuePropName="checked"
                >
                  <Checkbox className="!ml-0">
                    {t("profile.DriverAppSettings.canChangeAddressForOngoingOrders")}
                  </Checkbox>
                </FormItem>
              </div>
            </div>
            <div className="flex gap-x-3">
              <FormItem name="triggerReturnAfterFailure" className="mb-0" valuePropName="checked">
                <Switch />
              </FormItem>
              <LabelWithTooltip
                label={t("profile.DriverAppSettings.triggerReturnAfterFailure")}
                tooltipContent={t("profile.DriverAppSettings.triggerReturnAfterFailureTooltip")}
              />
            </div>
            <div className="flex gap-x-3">
              <FormItem name="hideDriverPayInTheApp" className="mb-0" valuePropName="checked">
                <Switch />
              </FormItem>
              <LabelWithTooltip
                label={t("profile.DriverAppSettings.hideDriverPayInTheApp")}
                tooltipContent={t("profile.DriverAppSettings.hideDriverPayInTheAppTooltip")}
              />
            </div>
            <div className="flex gap-x-3">
              <FormItem name="driverCanOptimizeRoutes" className="mb-0" valuePropName="checked">
                <Switch disabled={true} />
              </FormItem>
              <LabelWithTooltip
                label={t("profile.DriverAppSettings.driverCanOptimizeRoutes")}
                tooltipContent={t("profile.DriverAppSettings.driverCanOptimizeRoutesTooltip")}
              />
            </div>
            <div className="flex gap-x-4 gap-y-6 mt-6">
              <KosmoButton
                onClick={() => {
                  form.setFieldsValue(driverSettings.driverApp || emptyAppConfig)
                }}
                type="default"
                classname="font-semibold"
                isDisabled={!isModifying || isLoading}
              >
                {t("common.cancel")}
              </KosmoButton>
              <KosmoButton
                type="primary"
                classname="font-semibold"
                htmlType="submit"
                isLoading={isLoading}
                isDisabled={!isModifying}
              >
                {t("common.save")}
              </KosmoButton>
            </div>
          </Form>
        )}
      </SettingContentWrapper>
    </SettingSection>
  )
}

export default DriverAppSettings
